import { useState, useCallback } from 'react';

import {
  ProductRecognitionCropScanResponse,
  ProductRecognitionCropScanUpdate,
} from '../../../types';
import { ApiService } from '../../../services/api';
import { useAuthContext } from '../../../context/AuthContext';

export const useProductRecognitionScanning = (imageId: string) => {
  const { token } = useAuthContext();
  const [cropScans, setCropScans] = useState<
    ProductRecognitionCropScanResponse[]
  >([]);
  const [isGeneratingCropScans, setIsGeneratingCropScans] = useState(false);
  const [cropScansImageUrl, setCropScansImageUrl] = useState<string>('');
  const [isFetchingCropScans, setIsFetchingCropScans] = useState(false);
  const [cropScansError, setCropScansError] = useState<Error | null>(null);
  const [sessionStarted, setSessionStarted] = useState(false);

  // Function to start the scanning session
  const startSession: () => Promise<ProductRecognitionCropScanResponse[]> =
    useCallback(() => {
      if (!imageId || !token) {
        return Promise.resolve([]);
      }

      setIsFetchingCropScans(true);
      setCropScansError(null);
      setSessionStarted(true);

      return ApiService.getProductRecognitionCropScans(token, Number(imageId))
        .then((response) => {
          setCropScans(response.crop_scans);
          setCropScansImageUrl(response.image_url);
          setIsGeneratingCropScans(false);
          return response.crop_scans;
        })
        .catch((error) => {
          if (error.message.includes('404')) {
            // Create new crop scans if none exist
            setIsGeneratingCropScans(true);
            return ApiService.createProductRecognitionCropScans(
              token,
              Number(imageId)
            )
              .then((response) => {
                setCropScans(response.crop_scans);
                setCropScansImageUrl(response.image_url);
                setIsGeneratingCropScans(false);
                return response.crop_scans;
              })
              .catch((createError) => {
                setCropScansError(createError);
                setIsGeneratingCropScans(false);
                return [];
              });
          } else {
            setCropScansError(error);
          }

          return [];
        })
        .finally(() => {
          setIsFetchingCropScans(false);
        });
    }, [imageId, token]);

  // Function to save selected crop scans
  const createEmbeddings = useCallback(
    async (cropScans: ProductRecognitionCropScanUpdate[]) => {
      if (!imageId || !token || cropScans.length === 0) return;
      setIsFetchingCropScans(true);
      setCropScansError(null);
      let isSuccess = false;

      try {
        const saveCropScans = async () => {
          try {
            await ApiService.updateProductRecognitionCropScans(
              token,
              Number(imageId),
              cropScans
            );
          } catch {}
        };

        await Promise.all([
          ApiService.createEmbeddings(token, imageId, cropScans),
          saveCropScans(),
        ]);
        isSuccess = true;
      } catch (error) {
        setCropScansError(
          error instanceof Error
            ? error
            : new Error('Failed to save create embeddings')
        );
      } finally {
        setIsFetchingCropScans(false);
      }

      return isSuccess;
    },
    [imageId, token]
  );

  return {
    createEmbeddings,
    cropScansError,
    cropScans,
    cropScansImageUrl,
    isFetchingCropScans,
    isGeneratingCropScans,
    sessionStarted,
    startSession,
    setCropScans,
  };
};
